import type { ReactNode } from "react"
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useQuery } from "@rocicorp/zero/react"
import { useLocation, useNavigate } from "@tanstack/react-router"
import { AnimatePresence, motion } from "motion/react"
import { IconScrollTextFill18 } from "nucleo-ui-fill-18"
import {
	IconAtom2Outline18,
	IconMegaphoneOutline18,
	IconXmarkOutline18,
	IconMicrophone2Outline18 as Microphone,
} from "nucleo-ui-outline-18"

import type {
	AiChatMessage,
	AICiteItem,
	AiContextAttachment,
	CopilotScope,
} from "@productlane/lib/src/ai"
import type {
	AgentSetupActionArgs,
	AiChatFeedbackInput,
} from "@productlane/ui/src/components/ai-chat"
import { AI_CITATION_TYPE_DOCS, copilotScopeKey } from "@productlane/lib/src/ai"
import { cn } from "@productlane/lib/src/cn"
import {
	Button,
	ChatInput,
	IconButton,
	NucleoFillIcon,
	Tooltip,
} from "@productlane/ui"
import {
	AiChatAutoScrollContainer,
	AiChatBubbleAssistant,
	AiChatBubbleUser,
	AiChatMessageActions,
	AiChatMessageEnrichments,
	ContentRewriteCard,
	DocsProposalStatusRow,
} from "@productlane/ui/src/components/ai-chat"
import {
	composeMessageWithPastes,
	createTextPaste,
	isLongPaste,
} from "@productlane/ui/src/components/chat-input-text-paste"
import { LinearIcon } from "@productlane/ui/src/icons/linear-logo"
import { AiAssistantIcon } from "@productlane/ui/src/icons/project/ai-assistant-icon"
import { queries } from "@productlane/zero/queries"

import type { AgentSetupCardPatch } from "./agent-setup-card-state"
import type { CopilotComposerHandle } from "./context-mentions/copilot-composer-editor"
import type { CopilotRewritePreview } from "./copilot-rewrite-preview-dialog"
import type { DocsProposalOutcome } from "./docs-proposal-outcomes"
import type { CopilotPageContext } from "./sidebar-ai-chat-sync"
import { toast } from "@/components/toast"
import { analytics } from "@/lib/analytics"
import { api } from "@/lib/api"
import { buildContinueInClaudeLink } from "@/lib/build-continue-in-claude-link"
import { captureError } from "@/lib/capture-error"
import { generateId } from "@/lib/id"
import { openDeepLink } from "@/lib/open-deep-link"
import { useCopilotState } from "@/lib/states/copilot-state"
import { transcribeAssistantAudio } from "@/lib/transcribe-assistant-audio"
import { useAuthData } from "@/lib/zero"
import { AgentHomeTodos } from "@/screens/app/agent/agent-home-todos"
import { previewAiHtml } from "../../center-content/bottom-bar/render-ai-draft"
import {
	agentSetupFailureToast,
	applyAgentSetupCardPatch,
} from "./agent-setup-card-state"
import { ContextMentionMessageContent } from "./context-mentions/context-mention-message-content"
import { CopilotComposerEditor } from "./context-mentions/copilot-composer-editor"
import { COPILOT_COMPOSER_FADE_CLASSES } from "./copilot-composer-chrome"
import {
	COPILOT_CONTEXT_ROW_CLASSES,
	COPILOT_CONTEXT_WRAPPER_CLASSES,
	copilotContextPillLabel,
} from "./copilot-context-pill"
import {
	COPILOT_EMPTY_STATE_CLASSES,
	COPILOT_EMPTY_STATE_TITLE,
	COPILOT_QUICK_ACTION_GROUP_CLASSES,
	COPILOT_QUICK_ACTION_ICON_CLASSES,
	COPILOT_QUICK_ACTION_ROW_CLASSES,
	copilotEmptyStateDescription,
	isAgentHome,
} from "./copilot-empty-state"
import { actionlessAnswerId } from "./copilot-pinned-actions"
import { copilotQuickActions } from "./copilot-quick-actions"
import { buildRewritePreview } from "./copilot-rewrite-preview"
import { CopilotRewritePreviewDialog } from "./copilot-rewrite-preview-dialog"
import { DocsProposalCards } from "./docs-proposal-cards"
import {
	applyDocsProposalOutcome,
	docsProposalOutcome,
	readyDocsProposalChanges,
} from "./docs-proposal-outcomes"
import { internalLinkHref } from "./internal-link-href"
import { shouldFocusCopilotComposer } from "./sidebar-ai-chat-focus"
import { SidebarAiChatHeader } from "./sidebar-ai-chat-header"
import { SidebarAiChatMcpSelector } from "./sidebar-ai-chat-mcp-selector"
import { useSidebarAiChatState } from "./sidebar-ai-chat-state"
import {
	SidebarAiChatSync,
	stopActiveSidebarAiChatStream,
} from "./sidebar-ai-chat-sync"
import { useAgentMcpSections } from "./use-agent-mcp-sections"
import { voiceErrorDescription, voiceErrorTitle } from "./voice-error-copy"

// The group row lands only when the run ends, so it is both the "ready" signal
// and, when it closes empty, the "nothing to change" one.
const DocsProposalRunWatcher = memo(function DocsProposalRunWatcher({
	runId,
	messageId,
	onOutcome,
}: {
	runId: string
	messageId: string
	onOutcome: (messageId: string, outcome: DocsProposalOutcome) => void
}) {
	const [group] = useQuery(queries.proposalGroupById(runId))

	useEffect(() => {
		const outcome = docsProposalOutcome(group)
		if (outcome) onOutcome(messageId, outcome)
	}, [group, messageId, onOutcome])

	return null
})

// A mask instead of a gradient overlay, so the fade matches whatever pane sits behind it.
const EMBEDDED_SCROLL_CLASSES =
	"[scrollbar-gutter:stable] [mask-image:linear-gradient(to_bottom,transparent,black_24px)]"
// Claude's message bubble: 12px by 16px, a 12px radius, 15/22 text.
const EMBEDDED_USER_BUBBLE_CLASSES =
	"!max-w-full !rounded-xl !px-4 !py-3 !leading-[22px]"

const NO_MESSAGES: AiChatMessage[] = []

const SidebarAiChatContent = ({
	panelScope,
	contextTitle,
	contextIcon,
	getPageContext,
	leading,
	emptyActions,
	embedded = false,
	startAtTop = false,
}: {
	panelScope: CopilotScope
	contextTitle?: string
	contextIcon?: string | null
	getPageContext?: () => CopilotPageContext | undefined
	leading?: ReactNode
	// Replaces the quick actions under the empty state's intro.
	emptyActions?: ReactNode
	embedded?: boolean
	// Opens on the leading message, even with a conversation under it.
	startAtTop?: boolean
}) => {
	const {
		messages,
		messageValue,
		setMessageValue,
		setMessages,
		scope,
		isHydratingHistory,
		pastedTexts,
		setPastedTexts,
	} = useSidebarAiChatState()
	const setPendingContentRewrite = useSidebarAiChatState(
		(s) => s.setPendingContentRewrite,
	)
	const pageContextDismissed = useSidebarAiChatState(
		(s) => s.pageContextDismissed,
	)
	const setPageContextDismissed = useSidebarAiChatState(
		(s) => s.setPageContextDismissed,
	)
	const navigate = useNavigate()
	const location = useLocation()
	const authData = useAuthData()
	const mcpSections = useAgentMcpSections()

	const navigateInternalLink = useCallback(
		(e: React.SyntheticEvent) => {
			const href = internalLinkHref(e)
			if (!href) return
			e.preventDefault()
			void navigate({ href })
		},
		[navigate],
	)
	const executeStripeActionMutation =
		api.billingStripeActions.execute.useMutation()
	const rejectStripeActionMutation =
		api.billingStripeActions.reject.useMutation()
	const executePostgresActionMutation =
		api.postgresActions.execute.useMutation()
	const rejectPostgresActionMutation = api.postgresActions.reject.useMutation()
	const createProposedIssueMutation =
		api.agentSetupActions.createLinearIssue.useMutation()
	const saveProposedInstructionsMutation =
		api.agentSetupActions.saveInstructions.useMutation()
	const rejectAgentSetupActionMutation =
		api.agentSetupActions.reject.useMutation()

	const skipAnimationIdsRef = useRef<Set<string>>(new Set())
	const lastSeenIdsRef = useRef<Set<string>>(new Set())
	const wasHydratingRef = useRef(isHydratingHistory)

	// The panel remounts when the user moves to the next article, entry, or
	// thread. Whatever is on screen at that first render is carried-over content,
	// so it appears as-is; later changes still animate.
	const isFirstRenderRef = useRef(true)
	useEffect(() => {
		isFirstRenderRef.current = false
	}, [])

	const composerRef = useRef<CopilotComposerHandle | null>(null)
	const [attachedContext, setAttachedContext] = useState<AiContextAttachment[]>(
		[],
	)
	const pendingFocusScopeKey = useCopilotState((s) => s.pendingFocusScopeKey)
	const consumeFocusRequest = useCopilotState((s) => s.consumeFocusRequest)
	const wantsFocus = shouldFocusCopilotComposer({
		panelScope,
		isComposerMounted: messages !== null,
		pendingFocusScopeKey,
	})
	useEffect(() => {
		if (!wantsFocus) return
		composerRef.current?.focus()
		consumeFocusRequest()
	}, [wantsFocus, consumeFocusRequest])
	const wantsVoice = useCopilotState(
		(s) =>
			messages !== null &&
			s.pendingVoiceScopeKey === copilotScopeKey(panelScope),
	)
	const consumeVoiceRequest = useCopilotState((s) => s.consumeVoiceRequest)

	// Starting a new chat or switching surface empties the store's draft; the
	// editor holds its own doc, so it has to follow.
	useEffect(() => {
		if (messageValue === "") composerRef.current?.clear()
	}, [messageValue])

	// A link source opens as an anchor in the list itself, so only articles that
	// live in Productlane reach this.
	const handleSourceClick = useCallback(
		(source: AICiteItem) => {
			if (source.type === AI_CITATION_TYPE_DOCS) {
				void navigate({ to: "/edit-docs/$id", params: { id: source.id } })
			}
		},
		[navigate],
	)

	const handleContinueInClaude = useCallback(() => {
		const currentScope = useSidebarAiChatState.getState().scope
		if (currentScope?.kind !== "thread") return
		const { deepLink, prompt } = buildContinueInClaudeLink({
			messages: useSidebarAiChatState.getState().messages ?? [],
			threadId: currentScope.threadId,
			threadTitle: currentScope.threadTitle ?? "",
		})
		openDeepLink(deepLink)
		toast.success({
			title: "Opening in Claude Code",
			descriptionAction: {
				label: "Copy prompt",
				callback: () => void navigator.clipboard.writeText(prompt),
			},
		})
	}, [])

	const submitCopilotFeedbackMutation =
		api.ai.submitCopilotFeedback.useMutation()

	const handleFeedback = useCallback(
		async (messageId: string, { sentiment, comment }: AiChatFeedbackInput) => {
			const state = useSidebarAiChatState.getState()
			const sessionId = state.sessionId
			if (!sessionId) return
			const previous =
				state.messages?.find((m) => m.id === messageId)?.feedback?.sentiment ??
				null
			state.setMessageFeedback(messageId, sentiment)
			const send = () =>
				submitCopilotFeedbackMutation.mutateAsync({
					sessionId,
					messageId,
					sentiment,
					comment,
					scope: state.scope?.kind,
				})
			try {
				try {
					await send()
				} catch (error) {
					// The answer's row lands when the stream closes, so a rating on the
					// last word of a turn can beat it in.
					if (
						!(error instanceof Error && error.message.includes("not found"))
					) {
						throw error
					}
					await new Promise((resolve) => setTimeout(resolve, 1500))
					await send()
				}
				if (sentiment === "negative") {
					toast.success({
						title: "Feedback sent to the Productlane team",
						icon: IconAtom2Outline18,
					})
				}
			} catch (error) {
				useSidebarAiChatState.getState().setMessageFeedback(messageId, previous)
				toast.error({ title: "Could not send your feedback. Try again." })
				captureError(error, {
					domain: "ai-chat",
					action: "submit assistant feedback",
					workspaceId: authData.workspaceId,
					userId: authData.userId,
					extra: { messageId, sentiment },
				})
			}
		},
		[submitCopilotFeedbackMutation, authData],
	)

	const [rewritePreview, setRewritePreview] =
		useState<CopilotRewritePreview | null>(null)

	const openRewritePreview = useCallback(
		(rewrite: NonNullable<AiChatMessage["contentRewrite"]>) => {
			const preview = buildRewritePreview({
				rewrite,
				pageContext: getPageContext?.(),
				contextTitle,
				contextIcon,
			})
			if (!preview) return
			setRewritePreview(preview)
			analytics.track("copilot_rewrite_previewed", {
				target: rewrite.target,
				scope: scope?.kind ?? null,
			})
		},
		[getPageContext, contextTitle, contextIcon, scope],
	)

	const canPreviewRewrite = useCallback(
		(rewrite: NonNullable<AiChatMessage["contentRewrite"]>) =>
			buildRewritePreview({
				rewrite,
				pageContext: getPageContext?.(),
				contextTitle,
				contextIcon,
			}) !== null,
		[getPageContext, contextTitle, contextIcon],
	)

	const handleCopyAnswer = useCallback(() => {
		analytics.track("copilot_message_copied", {
			scope: useSidebarAiChatState.getState().scope?.kind ?? null,
		})
	}, [])

	const handleDocsProposalOutcome = useCallback(
		(messageId: string, outcome: DocsProposalOutcome) => {
			const current = useSidebarAiChatState.getState().messages
			if (!current) return
			const next = applyDocsProposalOutcome(current, messageId, outcome)
			if (next) useSidebarAiChatState.getState().setMessages(next)
		},
		[],
	)

	const updateStripeActionApproval = useCallback(
		(
			messageId: string,
			updater: (
				approval: NonNullable<AiChatMessage["stripeActionApproval"]>,
			) => NonNullable<AiChatMessage["stripeActionApproval"]>,
		) => {
			const current = useSidebarAiChatState.getState().messages
			if (!current) return
			let didChange = false
			const next = current.map((m) => {
				if (m.id !== messageId || !m.stripeActionApproval) return m
				const updated = updater(m.stripeActionApproval)
				if (updated === m.stripeActionApproval) return m
				didChange = true
				return { ...m, stripeActionApproval: updated }
			})
			if (didChange) {
				useSidebarAiChatState.getState().setMessages(next)
			}
		},
		[],
	)

	const handleApproveStripeAction = useCallback(
		async ({
			messageId,
			pendingActionId,
		}: {
			messageId: string
			pendingActionId: string
		}) => {
			updateStripeActionApproval(messageId, (s) => ({
				...s,
				status: "executing",
				errorMessage: undefined,
			}))
			try {
				const result = await executeStripeActionMutation.mutateAsync({
					pendingActionId,
				})
				updateStripeActionApproval(messageId, (s) => ({
					...s,
					status: "executed",
					subscriptionUrl: result.subscriptionUrl,
				}))
				toast.success({ title: "Stripe action executed" })
			} catch (error) {
				updateStripeActionApproval(messageId, (s) => ({
					...s,
					status: "errored",
					errorMessage:
						error instanceof Error ? error.message : "Failed to execute action",
				}))
				toast.error({ title: "Failed to execute Stripe action" })
			}
		},
		[executeStripeActionMutation, updateStripeActionApproval],
	)

	const handleRejectStripeAction = useCallback(
		async ({
			messageId,
			pendingActionId,
		}: {
			messageId: string
			pendingActionId: string
		}) => {
			updateStripeActionApproval(messageId, (s) => ({
				...s,
				status: "rejected",
			}))
			try {
				await rejectStripeActionMutation.mutateAsync({ pendingActionId })
			} catch (error) {
				console.error("Failed to record Stripe action rejection", error)
				captureError(error, {
					domain: "ai-chat",
					action: "record stripe action rejection",
					workspaceId: authData.workspaceId,
					userId: authData.userId,
					extra: { messageId, pendingActionId },
				})
			}
		},
		[rejectStripeActionMutation, updateStripeActionApproval, authData],
	)

	const updatePostgresActionApproval = useCallback(
		(
			messageId: string,
			updater: (
				approval: NonNullable<AiChatMessage["postgresActionApproval"]>,
			) => NonNullable<AiChatMessage["postgresActionApproval"]>,
		) => {
			const current = useSidebarAiChatState.getState().messages
			if (!current) return
			let didChange = false
			const next = current.map((m) => {
				if (m.id !== messageId || !m.postgresActionApproval) return m
				const updated = updater(m.postgresActionApproval)
				if (updated === m.postgresActionApproval) return m
				didChange = true
				return { ...m, postgresActionApproval: updated }
			})
			if (didChange) {
				useSidebarAiChatState.getState().setMessages(next)
			}
		},
		[],
	)

	const handleApprovePostgresAction = useCallback(
		async ({
			messageId,
			pendingActionId,
			saveWhitelist,
		}: {
			messageId: string
			pendingActionId: string
			saveWhitelist: boolean
		}) => {
			updatePostgresActionApproval(messageId, (s) => ({
				...s,
				status: "executing",
				errorMessage: undefined,
			}))
			try {
				const { result, savedWhitelistName } =
					await executePostgresActionMutation.mutateAsync({
						pendingActionId,
						saveWhitelist,
					})
				updatePostgresActionApproval(messageId, (s) => ({
					...s,
					status: "executed",
					result,
					savedWhitelistName: savedWhitelistName ?? null,
				}))
				toast.success({
					title: savedWhitelistName
						? `Saved as "${savedWhitelistName}"`
						: "Query executed",
				})
				// Hand off to the sync layer: it will POST a continuation turn
				// using the cached result so the model writes a real answer.
				useSidebarAiChatState
					.getState()
					.setPendingPostgresContinuation(pendingActionId)
			} catch (error) {
				updatePostgresActionApproval(messageId, (s) => ({
					...s,
					status: "errored",
					errorMessage:
						error instanceof Error ? error.message : "Failed to run query",
				}))
				toast.error({ title: "Query failed, asking the assistant to fix it" })
				// Hand off to the sync layer: it streams a follow-up turn that feeds
				// the SQL error back to the model so it proposes a corrected query.
				useSidebarAiChatState
					.getState()
					.setPendingPostgresContinuation(pendingActionId)
			}
		},
		[executePostgresActionMutation, updatePostgresActionApproval],
	)

	const handleRejectPostgresAction = useCallback(
		async ({
			messageId,
			pendingActionId,
		}: {
			messageId: string
			pendingActionId: string
		}) => {
			updatePostgresActionApproval(messageId, (s) => ({
				...s,
				status: "rejected",
			}))
			try {
				await rejectPostgresActionMutation.mutateAsync({ pendingActionId })
			} catch (error) {
				console.error("Failed to record Postgres action rejection", error)
				captureError(error, {
					domain: "ai-chat",
					action: "record postgres action rejection",
					workspaceId: authData.workspaceId,
					userId: authData.userId,
					extra: { messageId, pendingActionId },
				})
			}
		},
		[rejectPostgresActionMutation, updatePostgresActionApproval, authData],
	)

	const handleApproveAgentSetupAction = useCallback(
		async ({ messageId, pendingActionId, kind }: AgentSetupActionArgs) => {
			const patch = (card: Partial<AgentSetupCardPatch>) => {
				const current = useSidebarAiChatState.getState().messages
				if (!current) return
				useSidebarAiChatState
					.getState()
					.setMessages(applyAgentSetupCardPatch(current, messageId, kind, card))
			}
			patch({ status: "executing", errorMessage: undefined })
			try {
				if (kind === "linear_issue") {
					const created = await createProposedIssueMutation.mutateAsync({
						pendingActionId,
					})
					patch({ status: "executed", created })
					toast.success({
						title: `Created ${created.identifier}`,
						icon: LinearIcon,
					})
				} else {
					await saveProposedInstructionsMutation.mutateAsync({
						pendingActionId,
					})
					patch({ status: "executed" })
					toast.success({
						title: "Agent instructions saved",
						icon: IconScrollTextFill18,
					})
				}
			} catch (error) {
				const { type, title, description } = agentSetupFailureToast(kind, error)
				patch({ status: "errored", errorMessage: description ?? title })
				toast[type]({
					title,
					description,
					icon: kind === "linear_issue" ? LinearIcon : IconScrollTextFill18,
				})
				if (type === "error") {
					captureError(error, {
						domain: kind === "linear_issue" ? "linear" : "ai-chat",
						action: "run setup proposal",
						workspaceId: authData.workspaceId,
						userId: authData.userId,
						extra: { messageId, pendingActionId, kind },
					})
				}
			}
		},
		[createProposedIssueMutation, saveProposedInstructionsMutation, authData],
	)

	const handleRejectAgentSetupAction = useCallback(
		async ({ messageId, pendingActionId, kind }: AgentSetupActionArgs) => {
			const current = useSidebarAiChatState.getState().messages
			if (current) {
				useSidebarAiChatState.getState().setMessages(
					applyAgentSetupCardPatch(current, messageId, kind, {
						status: "rejected",
					}),
				)
			}
			try {
				await rejectAgentSetupActionMutation.mutateAsync({ pendingActionId })
			} catch (error) {
				captureError(error, {
					domain: "ai-chat",
					action: "record setup proposal rejection",
					workspaceId: authData.workspaceId,
					userId: authData.userId,
					extra: { messageId, pendingActionId, kind },
				})
			}
		},
		[rejectAgentSetupActionMutation, authData],
	)

	// Ready cards keep watching: a reviewer accepting or rejecting a change
	// while the chat is open should empty the list rather than leave a dead link.
	const docsRunWatchers = useMemo(
		() =>
			(messages ?? []).flatMap((m) => {
				const s = m.docsProposalSuggestion
				if (!s?.proposalGroupId) return []
				if (s.status !== "generating" && s.status !== "ready") return []
				return [{ messageId: m.id, runId: s.proposalGroupId }]
			}),
		[messages],
	)

	const visibleMessages = messages ?? NO_MESSAGES
	const actionlessId = actionlessAnswerId(visibleMessages)

	// On every render, diff the current message list against the previous one
	// and mark any IDs added in batches of >1 as "skip animation" (these are
	// history hydrations rather than freshly authored messages). Everything
	// present on the first render is a restored conversation too, as is a single
	// message that arrives with the hydration: replaying its entrance on every
	// navigation reads as a glitch.
	const hydrationLanded = wasHydratingRef.current && !isHydratingHistory
	wasHydratingRef.current = isHydratingHistory
	const newIds: string[] = []
	for (const m of visibleMessages) {
		if (!lastSeenIdsRef.current.has(m.id)) newIds.push(m.id)
	}
	if (newIds.length > 1 || isFirstRenderRef.current || hydrationLanded) {
		for (const id of newIds) skipAnimationIdsRef.current.add(id)
	}
	lastSeenIdsRef.current = new Set(visibleMessages.map((m) => m.id))

	const lastMessage =
		visibleMessages.length > 0
			? visibleMessages[visibleMessages.length - 1]
			: null
	const isProcessing =
		lastMessage?.role === "assistant" && !lastMessage?.processed

	const handleSubmit = (value: string) => {
		const content = composeMessageWithPastes(value, pastedTexts)
		if (!content || isProcessing) return
		const newMessage: AiChatMessage = {
			id: generateId(),
			role: "user",
			content,
			processed: false,
			createdAt: new Date(),
			sources: [],
			actions: [],
			...(attachedContext.length > 0
				? { attachedContext: [...attachedContext] }
				: {}),
		}
		setMessages([...(messages ?? []), newMessage])
		composerRef.current?.clear()
		setMessageValue("")
		setAttachedContext([])
		setPastedTexts([])
	}

	const handleSendMessage = (text: string) => {
		if (isProcessing) return
		const newMessage: AiChatMessage = {
			id: generateId(),
			role: "user",
			content: text,
			processed: false,
			createdAt: new Date(),
			sources: [],
			actions: [],
		}
		setMessages([...(messages ?? []), newMessage])
	}

	const quickActions = copilotQuickActions(
		scope,
		location.pathname.startsWith("/agent/") ? "agent-chat" : null,
	)
	const showQuickActions =
		visibleMessages.length === 0 &&
		!isHydratingHistory &&
		!emptyActions &&
		quickActions.length > 0

	const contextPillLabel = pageContextDismissed
		? null
		: copilotContextPillLabel(panelScope, contextTitle)
	const contextPillIcon = contextIcon ? (
		<NucleoFillIcon
			icon={contextIcon}
			className="h-3.5 w-3.5 flex-none text-gray-400 dark:text-gray-500"
		/>
	) : (
		<IconMegaphoneOutline18 className="h-3.5 w-3.5 flex-none text-gray-400 dark:text-gray-500" />
	)

	const placeholder =
		scope?.kind === "article"
			? "Ask about this article..."
			: scope?.kind === "changelog"
				? "Ask about this changelog..."
				: scope?.kind === "reporting"
					? "Ask what to improve..."
					: scope?.kind === "agent" && !isAgentHome(scope)
						? "Ask for a change..."
						: scope?.kind === "workspace" || isAgentHome(scope)
							? "Ask the assistant..."
							: "Ask about this thread..."

	// A full-page chat scrolls the whole pane and centers a 768px column, like Claude.
	const messageColumnClass = embedded
		? "mx-auto flex w-full max-w-[768px] flex-col px-4 pb-8 pt-6"
		: "flex flex-col px-4 pb-5 pt-4"

	return (
		<div className={cn("relative flex h-full flex-col", !embedded && "pt-12")}>
			{!embedded && (
				<>
					<SidebarAiChatHeader />
					<div
						aria-hidden
						className="pointer-events-none absolute inset-x-0 top-12 z-10 h-5 bg-gradient-to-b from-gray-50 via-gray-50/80 to-transparent dark:from-gray-850 dark:via-gray-850/80"
					/>
				</>
			)}
			{docsRunWatchers.map((w) => (
				<DocsProposalRunWatcher
					key={w.runId}
					runId={w.runId}
					messageId={w.messageId}
					onOutcome={handleDocsProposalOutcome}
				/>
			))}
			<div
				role="presentation"
				className="flex-1 overflow-hidden"
				onClick={navigateInternalLink}
				onKeyDown={(e) => {
					if (e.key === "Enter" || e.key === " ") navigateInternalLink(e)
				}}
			>
				{visibleMessages.length === 0 && leading ? (
					<AiChatAutoScrollContainer
						className={messageColumnClass}
						scrollClassName={embedded ? EMBEDDED_SCROLL_CLASSES : undefined}
						lastMessage={null}
						startAtTop
					>
						{leading}
					</AiChatAutoScrollContainer>
				) : visibleMessages.length === 0 ? (
					<div className={COPILOT_EMPTY_STATE_CLASSES}>
						{!isHydratingHistory && (
							<>
								<motion.div
									initial={
										isFirstRenderRef.current ? false : { opacity: 0, y: 6 }
									}
									animate={{ opacity: 1, y: 0 }}
									transition={{ duration: 0.15, delay: 0.05 }}
									className="flex flex-col items-center text-center"
								>
									<div className="flex h-10 w-10 items-center justify-center rounded-[11px] border border-gray-200 bg-white dark:border-gray-700/80 dark:bg-gray-800/60">
										<AiAssistantIcon className="h-5 w-5 flex-none text-tertiary" />
									</div>
									<h2 className="mt-4 select-none text-[15px] font-semibold tracking-tight text-primary">
										{COPILOT_EMPTY_STATE_TITLE}
									</h2>
									<p className="mt-1 select-none text-balance text-[13px] text-tertiary">
										{copilotEmptyStateDescription(scope)}
									</p>
								</motion.div>
								{emptyActions && (
									<motion.div
										initial={
											isFirstRenderRef.current ? false : { opacity: 0, y: 6 }
										}
										animate={{ opacity: 1, y: 0 }}
										transition={{ duration: 0.15, delay: 0.1 }}
										className="mt-5 flex w-full justify-center"
									>
										{emptyActions}
									</motion.div>
								)}
							</>
						)}
					</div>
				) : (
					<AiChatAutoScrollContainer
						className={messageColumnClass}
						scrollClassName={embedded ? EMBEDDED_SCROLL_CLASSES : undefined}
						lastMessage={lastMessage}
						startAtTop={startAtTop}
					>
						{leading}
						<AnimatePresence>
							{visibleMessages.map((message, index) => {
								const isLast = index === visibleMessages.length - 1
								const isStreaming =
									isLast && message.role === "assistant" && !message.processed

								const skipBubbleAnimation = skipAnimationIdsRef.current.has(
									message.id,
								)
								const docsChanges = readyDocsProposalChanges(message)
								return message.role === "user" ? (
									<div key={message.id}>
										<AiChatBubbleUser
											message={message}
											className={
												embedded
													? "!max-w-[85%] !text-[15px]"
													: "!ml-0 !w-full !max-w-full !text-[0.85rem]"
											}
											bodyClassName={
												embedded ? EMBEDDED_USER_BUBBLE_CLASSES : undefined
											}
											disableEntranceAnimation={skipBubbleAnimation}
											contentSlot={
												message.attachedContext?.length ? (
													<ContextMentionMessageContent
														content={message.content}
														attachments={message.attachedContext}
													/>
												) : undefined
											}
										/>
									</div>
								) : (
									<div key={message.id} className="group/message mt-2">
										<AiChatBubbleAssistant
											message={message}
											isStreaming={isStreaming}
											thinkingLabel="Thinking"
											editorClasses="!text-[0.85rem] dark:!text-gray-300"
											disableEntranceAnimation={skipBubbleAnimation}
											header={
												!isStreaming &&
												message.docsProposalSuggestion && (
													<DocsProposalStatusRow
														summary={message.docsProposalSuggestion.summary}
														issueIdentifiers={
															message.docsProposalSuggestion.issueIdentifiers
														}
														issueCount={
															message.docsProposalSuggestion.issueIds.length
														}
														prUrls={message.docsProposalSuggestion.prUrls}
														status={
															message.docsProposalSuggestion.status ??
															"generating"
														}
														changes={message.docsProposalSuggestion.changes}
														rationale={message.docsProposalSuggestion.rationale}
														startedAt={message.createdAt}
														finishedAt={
															message.docsProposalSuggestion.finishedAt
														}
														errorMessage={
															message.docsProposalSuggestion.errorMessage
														}
													/>
												)
											}
											enrichments={
												<AiChatMessageEnrichments
													message={message}
													index={index}
													isStreaming={isStreaming}
													aiMessages={visibleMessages}
													showSuggestions={false}
													showActions={false}
													onSendMessage={handleSendMessage}
													onSourceClick={handleSourceClick}
													showFeedback={false}
													isProcessing={isProcessing}
													onContinueInClaude={handleContinueInClaude}
													onApproveStripeAction={handleApproveStripeAction}
													onRejectStripeAction={handleRejectStripeAction}
													onApprovePostgresAction={handleApprovePostgresAction}
													onRejectPostgresAction={handleRejectPostgresAction}
													onApproveAgentSetupAction={
														handleApproveAgentSetupAction
													}
													onRejectAgentSetupAction={
														handleRejectAgentSetupAction
													}
												/>
											}
										/>
										{message.agentTodos && message.processed && (
											<div className="mt-3">
												<AgentHomeTodos />
											</div>
										)}
										{docsChanges && (
											<DocsProposalCards
												changes={docsChanges}
												groupId={
													message.docsProposalSuggestion?.resolvedGroupId ??
													message.docsProposalSuggestion?.proposalGroupId
												}
											/>
										)}
										{message.contentRewrite && message.processed && (
											<ContentRewriteCard
												rewrite={{
													...message.contentRewrite,
													html: previewAiHtml(
														message.contentRewrite.target,
														message.contentRewrite.html,
													),
												}}
												onApply={() => {
													setPendingContentRewrite({
														target: message.contentRewrite!.target,
														html: message.contentRewrite!.html,
													})
													analytics.track("copilot_rewrite_applied", {
														target: message.contentRewrite!.target,
														scope: scope?.kind ?? null,
													})
												}}
												onPreview={
													canPreviewRewrite(message.contentRewrite)
														? () => openRewritePreview(message.contentRewrite!)
														: undefined
												}
											/>
										)}
										{message.capReached && (
											<div className="mt-2">
												<Button
													variant="outline"
													onClick={() =>
														void navigate({ to: "/settings/ai/usage" })
													}
												>
													Raise AI spend limit
												</Button>
											</div>
										)}
										{message.processed &&
											message.content &&
											message.id !== actionlessId && (
												<div className="-translate-y-1 px-1">
													<AiChatMessageActions
														message={message}
														sentiment={message.feedback?.sentiment ?? null}
														onFeedback={(feedback) =>
															void handleFeedback(message.id, feedback)
														}
														onCopy={handleCopyAnswer}
														showIssueBadges={false}
													/>
												</div>
											)}
									</div>
								)
							})}
						</AnimatePresence>
					</AiChatAutoScrollContainer>
				)}
			</div>

			<div
				className={
					embedded
						? "relative mx-auto w-full max-w-[768px] px-4 pb-4"
						: "relative px-3 pb-3"
				}
			>
				<div aria-hidden className={COPILOT_COMPOSER_FADE_CLASSES} />
				{showQuickActions && (
					<motion.div
						initial={isFirstRenderRef.current ? false : { opacity: 0, y: 6 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.15, delay: 0.1 }}
						className={COPILOT_QUICK_ACTION_GROUP_CLASSES}
					>
						{quickActions.map(({ label, Icon, message }) => (
							<Button
								key={label}
								variant="reaction"
								size="sm"
								Icon={Icon}
								iconClassName={COPILOT_QUICK_ACTION_ICON_CLASSES}
								disabled={isProcessing}
								onClick={() => handleSendMessage(message ?? label)}
								className={COPILOT_QUICK_ACTION_ROW_CLASSES}
							>
								<span className="truncate">{label}</span>
							</Button>
						))}
					</motion.div>
				)}
				<div
					className={cn(
						"rounded-xl",
						contextPillLabel && COPILOT_CONTEXT_WRAPPER_CLASSES,
					)}
				>
					{contextPillLabel && (
						<div className={COPILOT_CONTEXT_ROW_CLASSES}>
							{contextPillIcon}
							<span
								className="min-w-0 flex-1 truncate text-xs font-medium leading-none tracking-tight text-secondary"
								title={contextPillLabel}
							>
								{contextPillLabel}
							</span>
							<Tooltip label="Remove from context" side="bottom">
								<IconButton
									Icon={IconXmarkOutline18}
									iconStrokeWidth={1.5}
									variant="ghost"
									size="md"
									aria-label="Remove from context"
									onClick={() => setPageContextDismissed(true)}
									className="flex-none opacity-0 focus-visible:opacity-100 group-hover/context:opacity-100"
									iconClassName="h-3.5 w-3.5"
								/>
							</Tooltip>
						</div>
					)}
					<div className="rounded-xl shadow-[0_1px_6px_rgba(0,0,0,0.04)] dark:shadow-[0_1px_6px_rgba(0,0,0,0.28)]">
						<ChatInput
							voiceInput={(text) => composerRef.current?.appendText(text)}
							transcribeAudio={transcribeAssistantAudio}
							autoStartVoice={wantsVoice}
							onAutoStartVoice={consumeVoiceRequest}
							onVoiceError={(error) =>
								toast.info({
									title: voiceErrorTitle(error),
									description: voiceErrorDescription(error),
									icon: Microphone,
								})
							}
							leadingActions={
								mcpSections.length > 0 ? (
									<SidebarAiChatMcpSelector available={mcpSections} />
								) : undefined
							}
							value={messageValue}
							setValue={setMessageValue}
							inputSlot={
								<CopilotComposerEditor
									ref={composerRef}
									placeholder={placeholder}
									className="-mt-1 max-h-[168px] min-h-[24px] !pt-0"
									onChange={(text, attachments) => {
										setMessageValue(text)
										setAttachedContext(attachments)
									}}
									onSubmit={() => handleSubmit(messageValue)}
									onLongPaste={(text) => {
										if (!isLongPaste(text)) return false
										setPastedTexts([...pastedTexts, createTextPaste(text)])
										return true
									}}
								/>
							}
							onRequestFocus={() => composerRef.current?.focus()}
							textPastes={pastedTexts}
							onTextPaste={(text) =>
								setPastedTexts([...pastedTexts, createTextPaste(text)])
							}
							onRemoveTextPaste={(id) =>
								setPastedTexts(pastedTexts.filter((p) => p.id !== id))
							}
							onSubmit={() => handleSubmit(messageValue)}
							isGenerating={isProcessing}
							onStop={stopActiveSidebarAiChatStream}
							placeholder={placeholder}
							inputId="sidebar-ai-chat"
							animateOnMount={false}
							className="text-sm"
							containerClassName="bg-white dark:bg-gray-800 rounded-xl ring-0 border border-black/15 dark:border-white/15"
							autoFocus={false}
							alwaysExpanded={true}
							submitIcon="arrow-up"
						/>
					</div>
				</div>
			</div>
			<CopilotRewritePreviewDialog
				preview={rewritePreview}
				onOpenChange={(open) => {
					if (!open) setRewritePreview(null)
				}}
				onApply={() => {
					if (!rewritePreview) return
					setPendingContentRewrite({
						target: rewritePreview.rewrite.target,
						html: rewritePreview.rewrite.html,
					})
					analytics.track("copilot_rewrite_applied", {
						target: rewritePreview.rewrite.target,
						scope: scope?.kind ?? null,
						from: "preview",
					})
					setRewritePreview(null)
				}}
			/>
		</div>
	)
}

export const SidebarAiChat = ({
	scope,
	getPageContext,
	contextTitle,
	contextIcon,
	leading,
	emptyActions,
	embedded,
	startAtTop,
}: {
	scope: CopilotScope
	getPageContext?: () => CopilotPageContext | undefined
	contextTitle?: string
	contextIcon?: string | null
	// Rendered above the messages, e.g. the Agent page's idea card.
	leading?: ReactNode
	emptyActions?: ReactNode
	// Inline in a page instead of the side panel: no panel header.
	embedded?: boolean
	startAtTop?: boolean
}) => {
	return (
		<SidebarAiChatSync scope={scope} getPageContext={getPageContext}>
			<SidebarAiChatContent
				panelScope={scope}
				contextTitle={contextTitle}
				contextIcon={contextIcon}
				getPageContext={getPageContext}
				leading={leading}
				emptyActions={emptyActions}
				embedded={embedded}
				startAtTop={startAtTop}
			/>
		</SidebarAiChatSync>
	)
}
